import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { EditProfileDialogComponent } from './edit-profile-dialog/edit-profile-dialog.component';
import { ChangePasswordDialogComponent } from './change-password-dialog/change-password-dialog.component';
import { SharedService } from '../services/shared.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-my-profile',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        MatCardModule,
        MatButtonModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './my-profile.component.html',
    styleUrl: './my-profile.component.scss',
})
export class MyProfileComponent implements OnInit {
    profile: ProfileData | null = null;
    loading = false;

    constructor(
        private router: Router,
        private sharedService: SharedService,
        private toastr: ToastrService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        if (!localStorage.getItem('token')) {
            localStorage.clear();
            this.router.navigate(['']);
            return;
        }
        this.loadProfile();
    }

    loadProfile(): void {
        this.loading = true;
        this.sharedService.getRequest('/user/profile').subscribe({
            next: (res: any) => {
                this.loading = false;
                this.profile = res.content;
            },
            error: (err) => {
                this.loading = false;
                const msg = err?.error?.message?.message ?? 'Something went wrong';
                this.toastr.error('Error: ' + msg);
                if (msg === 'Please login again. Your session is expired') {
                    localStorage.clear();
                    this.router.navigate(['/']);
                }
            },
        });
    }

    getInitials(): string {
        const f = this.profile?.first_name?.charAt(0)?.toUpperCase() ?? '';
        const l = this.profile?.last_name?.charAt(0)?.toUpperCase() ?? '';
        return f + l;
    }

    hasPermissions(): boolean {
        return this.profile?.roles?.some(r => r.permissions?.length > 0) ?? false;
    }

    openEditProfileDialog(): void {
        const ref = this.dialog.open(EditProfileDialogComponent, {
            width: '560px',
            panelClass: 'daxa-dialog',
            data: { profile: this.profile },
        });

        ref.afterClosed().subscribe((result: Partial<ProfileData> | undefined) => {
            if (result) {
                this.profile = { ...this.profile!, ...result };
                this.toastr.success('Profile updated successfully');
            }
        });
    }

    openChangePasswordDialog(): void {
        const ref = this.dialog.open(ChangePasswordDialogComponent, {
            width: '520px',
            panelClass: 'daxa-dialog',
        });

        ref.afterClosed().subscribe((success: boolean | undefined) => {
            if (success) {
                this.toastr.success('Password changed successfully');
            }
        });
    }
}

// ── Interfaces ───────────────────────────────────────────────────────────────

export interface ProfileData {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    is_active: string;
    is_superuser: boolean;
    created_at: string;
    updated_at: string;
    last_login: string;
    roles: Role[];
}

export interface Role {
    id: number;
    name: string;
    permissions: Permission[];
}

export interface Permission {
    name: string;
}